/*
 * Cart rewards controller.
 *
 * Two jobs:
 *   1. Paint the unified progress bar and the gift tier tiles from the current
 *      non-gift subtotal, animating the bar fill smoothly on every change.
 *   2. Enforce the gift rules by talking to the cart AJAX API:
 *        - at most one gift in the cart (adding a new one removes the others),
 *        - gift quantity capped at 1,
 *        - a gift whose tier threshold is no longer met is auto-removed, with a
 *          toast telling the customer they lost it.
 *
 * The script is loaded inside the cart notification snippet (rendered in the
 * header on every page), so enforcement runs site-wide: adding a gift anywhere
 * triggers a `cartUpdate`, which we react to.
 */
(function () {
  if (window.__cartRewardsInit) return;
  window.__cartRewardsInit = true;

  var GIFT_KEYS_STORE = 'cartRewardsGiftKeys';
  var busy = false;

  /* ----- helpers ----------------------------------------------------- */

  function parseConfig(el) {
    var node = el.querySelector('.cart-rewards__config');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function moneyFmt(cents) {
    // EUR, Italian formatting: 1234 -> "12,34 €"
    var v = (cents / 100).toFixed(2).replace('.', ',');
    return v + ' €';
  }

  function giftMinByVariant(config, variantId, productId) {
    for (var i = 0; i < config.gifts.length; i++) {
      var g = config.gifts[i];
      if (g.variantId === variantId || g.id === productId) return g.min;
    }
    return null;
  }

  // Is this /cart.js line item a gift? Prefer the catalog, fall back to the
  // hidden `_gift_min` property we attach when adding from the landing.
  function lineGiftMin(config, line) {
    var byCatalog = giftMinByVariant(config, line.variant_id, line.product_id);
    if (byCatalog != null) return byCatalog;
    var props = line.properties || {};
    if (props._gift_min != null) return parseInt(props._gift_min, 10) || 0;
    return null;
  }

  function reachedColor(config, subtotal) {
    var tiers = (config.tiers || []).slice().sort(function (a, b) { return a - b; });
    var reached = 0;
    for (var i = 0; i < tiers.length; i++) {
      if (subtotal >= tiers[i]) reached = i + 1;
    }
    if (reached > 0) return 't' + Math.min(reached, 3);
    if (subtotal >= config.freeShipping) return 'ship';
    return 'base';
  }

  function nextMilestone(config, subtotal) {
    var ms = (config.tiers || []).concat([config.freeShipping]).filter(function (v) { return v > 0; });
    ms.sort(function (a, b) { return a - b; });
    for (var i = 0; i < ms.length; i++) {
      if (ms[i] > subtotal) return ms[i];
    }
    return 0;
  }

  /* ----- painting ---------------------------------------------------- */

  function paint(el, subtotal, animated) {
    var config = parseConfig(el);
    if (!config) return;

    var fill = el.querySelector('[data-fill]');
    var maxMs = config.maxMilestone || 1;
    var pct = Math.min(100, Math.round((subtotal * 100) / maxMs));
    var targetWidth = pct + '%';

    // fill color
    el.setAttribute('data-fill-color', reachedColor(config, subtotal));

    // markers reached state
    el.querySelectorAll('.cart-rewards__marker').forEach(function (m) {
      var at = parseInt(m.getAttribute('data-marker'), 10);
      m.classList.toggle('is-reached', subtotal >= at);
    });

    // message
    var msg = el.querySelector('.cart-rewards__message');
    if (msg) {
      var next = nextMilestone(config, subtotal);
      if (next === 0) {
        msg.innerHTML = (config.tiers && config.tiers.length > 0)
          ? 'Hai sbloccato <strong>tutti i premi</strong>!'
          : '<strong>Spedizione gratuita</strong> sbloccata!';
      } else if (next === config.freeShipping) {
        msg.innerHTML = 'Ti mancano <strong>' + moneyFmt(next - subtotal) + '</strong> alla spedizione gratuita';
      } else {
        msg.innerHTML = 'Ti mancano <strong>' + moneyFmt(next - subtotal) + '</strong> al prossimo omaggio';
      }
    }

    // tiles
    el.querySelectorAll('.cart-rewards__tile').forEach(function (tile) {
      var at = parseInt(tile.getAttribute('data-tier'), 10);
      var unlocked = subtotal >= at;
      tile.classList.toggle('is-unlocked', unlocked);
      var state = tile.querySelector('[data-tier-state]');
      if (state) {
        if (!unlocked) {
          state.innerHTML = 'Ti mancano <strong data-tier-remaining>' + moneyFmt(at - subtotal) + '</strong>';
        } else if (tile.hasAttribute('data-ship')) {
          state.textContent = 'Sbloccata!';
        } else {
          state.textContent = 'Scegli il tuo omaggio';
        }
      }
    });

    // animate fill: always start empty (0) and rise to the target, so the bar
    // fills from zero on every update. Setting width to 0 with the transition
    // off and forcing a reflow commits the empty state before we re-enable the
    // transition, so there is no jump from the previous (or server-rendered)
    // width.
    if (!fill) return;
    if (animated) {
      fill.style.transition = 'none';
      fill.style.width = '0%';
      void fill.offsetWidth;
      fill.style.transition = '';
      requestAnimationFrame(function () {
        fill.style.width = targetWidth;
      });
    } else {
      fill.style.width = targetWidth;
    }
  }

  function paintAll(subtotal, animated) {
    document.querySelectorAll('cart-rewards').forEach(function (el) {
      paint(el, subtotal, animated);
    });
  }

  function updateSelectedGift(title) {
    document.querySelectorAll('[data-selected-gift]').forEach(function (node) {
      var strong = node.querySelector('[data-selected-gift-title]');
      if (title) {
        if (strong) strong.textContent = title;
        node.hidden = false;
      } else {
        node.hidden = true;
      }
    });
  }

  /* ----- toast ------------------------------------------------------- */

  function toast(message) {
    var node = document.createElement('div');
    node.className = 'cart-rewards-toast';
    node.setAttribute('role', 'status');
    node.textContent = message;
    document.body.appendChild(node);
    requestAnimationFrame(function () { node.classList.add('is-visible'); });
    setTimeout(function () {
      node.classList.remove('is-visible');
      setTimeout(function () { node.remove(); }, 350);
    }, 4200);
  }

  /* ----- cart helpers ------------------------------------------------ */

  function anyConfig() {
    var el = document.querySelector('cart-rewards');
    return el ? parseConfig(el) : null;
  }

  function getCart() {
    return fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function sectionsToRefresh() {
    var ids = [];
    if (document.getElementById('main-cart-footer')) ids.push('main-cart-footer');
    if (document.getElementById('main-cart-items')) ids.push('main-cart-items');
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    if (document.getElementById('cart-notification-freeshipping')) ids.push('cart-notification-freeshipping');
    return ids;
  }

  // POST /cart/change.js for a single line, optionally requesting sections back.
  function changeLine(key, quantity, withSections) {
    var body = { id: key, quantity: quantity };
    if (withSections) {
      body.sections = sectionsToRefresh().join(',');
      body.sections_url = window.location.pathname;
    }
    return fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function renderSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var html = new DOMParser().parseFromString(sections[id], 'text/html');
      if (id === 'cart-icon-bubble') {
        var bubble = document.getElementById('cart-icon-bubble');
        var src = html.querySelector('.shopify-section');
        if (bubble && src) bubble.innerHTML = src.innerHTML;
        return;
      }
      // For cart sections Dawn swaps the inner `.js-contents` of each section.
      var section = document.getElementById(id);
      if (!section) return;
      var incoming = html.getElementById(id) || html.querySelector('.shopify-section');
      if (!incoming) return;
      var targets = section.querySelectorAll('.js-contents');
      if (targets.length) {
        targets.forEach(function (t) {
          var match = incoming.querySelector('.js-contents');
          if (match) t.innerHTML = match.innerHTML;
        });
      } else {
        section.innerHTML = incoming.innerHTML;
      }
    });
  }

  /* ----- gift enforcement ------------------------------------------- */

  function loadPrevKeys() {
    try { return JSON.parse(sessionStorage.getItem(GIFT_KEYS_STORE)) || []; }
    catch (e) { return []; }
  }
  function savePrevKeys(keys) {
    try { sessionStorage.setItem(GIFT_KEYS_STORE, JSON.stringify(keys)); } catch (e) {}
  }

  // Returns a promise; resolves with the non-gift subtotal after any correction.
  function enforce(animated) {
    var config = anyConfig();
    if (!config) return Promise.resolve(null);

    return getCart().then(function (cart) {
      var items = cart.items || [];
      var gifts = [];
      var nonGiftSubtotal = 0;

      items.forEach(function (line) {
        var min = lineGiftMin(config, line);
        if (min != null) {
          gifts.push({ key: line.key, min: min, qty: line.quantity, title: line.product_title || line.title });
        } else {
          nonGiftSubtotal += line.final_line_price;
        }
      });

      var prevKeys = loadPrevKeys();
      var corrections = [];      // { key, quantity }
      var lostTitles = [];
      var keep = null;

      if (gifts.length > 0) {
        // Eligible gifts = tier threshold met by the current non-gift subtotal.
        var eligible = gifts.filter(function (g) { return nonGiftSubtotal >= g.min; });
        var ineligible = gifts.filter(function (g) { return nonGiftSubtotal < g.min; });

        // Decide which single gift to keep among the eligible ones.
        if (eligible.length > 0) {
          // Prefer a freshly-added gift (not in the previous key set).
          var fresh = eligible.filter(function (g) { return prevKeys.indexOf(g.key) === -1; });
          var pool = fresh.length > 0 ? fresh : eligible;
          // Among the pool, keep the highest tier (best gift).
          keep = pool.reduce(function (a, b) { return b.min >= a.min ? b : a; });
        }

        gifts.forEach(function (g) {
          if (g === keep) {
            if (g.qty > 1) corrections.push({ key: g.key, quantity: 1 }); // cap qty
            return;
          }
          // Remove every other gift (extra picks, or no-longer-eligible ones).
          corrections.push({ key: g.key, quantity: 0 });
          if (ineligible.indexOf(g) !== -1) lostTitles.push(g.title);
        });
      }

      if (corrections.length === 0) {
        // Steady state: just paint.
        paintAll(nonGiftSubtotal, animated);
        updateSelectedGift(keep ? keep.title : '');
        savePrevKeys(gifts.map(function (g) { return g.key; }));
        return nonGiftSubtotal;
      }

      // Apply corrections sequentially; request sections on the last call.
      busy = true;
      var chain = Promise.resolve();
      corrections.forEach(function (c, i) {
        chain = chain.then(function () {
          return changeLine(c.key, c.quantity, i === corrections.length - 1);
        });
      });

      return chain.then(function (last) {
        if (last && last.sections) renderSections(last.sections);
        return getCart();
      }).then(function (cart2) {
        var items2 = cart2.items || [];
        var sub2 = 0;
        var keptTitle = '';
        items2.forEach(function (line) {
          var min = lineGiftMin(config, line);
          if (min != null) keptTitle = line.product_title || line.title;
          else sub2 += line.final_line_price;
        });
        paintAll(sub2, animated);
        updateSelectedGift(keptTitle);
        savePrevKeys(items2.filter(function (l) { return lineGiftMin(config, l) != null; })
                          .map(function (l) { return l.key; }));
        if (lostTitles.length) {
          toast('Hai perso l’omaggio: ' + lostTitles.join(', ') + '. Aggiungi prodotti per riottenerlo.');
        }
        busy = false;
        return sub2;
      }).catch(function () { busy = false; });
    });
  }

  /* ----- landing: add gift from a tile button ------------------------ */

  function bindLandingButtons() {
    document.querySelectorAll('[data-gift-add]').forEach(function (btn) {
      if (btn.__bound) return;
      btn.__bound = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
        var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
        var min = parseInt(btn.getAttribute('data-min'), 10) || 0;
        btn.setAttribute('aria-busy', 'true');

        var config = anyConfig();
        getCart()
          .then(function (cart) {
            // Eligibility is based on the non-gift subtotal.
            var subtotal = 0;
            (cart.items || []).forEach(function (line) {
              if (config && lineGiftMin(config, line) != null) return; // skip gifts
              subtotal += line.final_line_price;
            });

            if (subtotal < min) {
              btn.removeAttribute('aria-busy');
              toast(
                'Per scegliere questo omaggio devi spendere almeno ' + moneyFmt(min) +
                '. Ti mancano ' + moneyFmt(min - subtotal) + '.'
              );
              return null; // not eligible: do not add
            }

            return fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [{ id: variantId, quantity: 1, properties: { _gift_min: String(min) } }]
              })
            })
              .then(function (r) { return r.json(); })
              .then(function () {
                publishCartUpdate('cart-rewards-gift');
                btn.removeAttribute('aria-busy');
                toast('Omaggio aggiunto al carrello!');
              });
          })
          .catch(function () { btn.removeAttribute('aria-busy'); });
      });
    });
  }

  function publishCartUpdate(source) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: source });
    } else {
      document.dispatchEvent(new CustomEvent('cart:rewards-refresh'));
    }
  }

  /* ----- wiring ------------------------------------------------------ */

  // First paint from the server-provided subtotal (no animation). This also
  // seeds host.dataset.rewardsLast so the first real update can animate from it.
  document.querySelectorAll('cart-rewards').forEach(function (el) {
    var config = parseConfig(el);
    if (config) {
      paint(el, config.subtotal, false);
    }
  });
  bindLandingButtons();

  // Run one enforcement pass on load to catch out-of-band gift state
  // (e.g. landing on /cart with a stale gift, or a full-page add).
  enforce(false);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      if (busy) return;
      bindLandingButtons();
      enforce(true);
    });
  }
  document.addEventListener('cart:rewards-refresh', function () {
    if (!busy) enforce(true);
  });
})();
